import { FC, useCallback, useEffect, useMemo } from 'react'

import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import { AssignmentDTO } from 'models/assignment'
import { DealDTO } from '../../../../models/deal'
import { AssignmentDetailDrawerContextProvider } from './AssignmentDetailDrawer.context'
import { GALLERY_DRAWER_HEADER_SX, ResponsiveDrawer } from 'components/common/ResponsiveDrawer'
import { GRAY_700 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { RendererErrorBoundary } from './common'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { choosePrimaryProduct } from 'utils/product'
import { getAssignmentRenderer } from './renderers'
import { logAnalyticsEvent } from 'utils/analytics/analyticsUtils'
import { useGalleryAssignment } from 'components/pages/Gallery/_main/contexts/GalleryAssignment.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

interface Props {
  /** Whether the drawer is open */
  isOpen: boolean
  /** Callback to close the drawer */
  onClose: () => void
  /** Assignment to display details for */
  assignment: AssignmentDTO | null
  /** Parent order ID */
  orderId: number
  /** Workspace that owns the deal — used to scope all per-assignment detail queries. */
  workspaceId: string
  /** Parent order data */
  order: DealDTO | null
}

/**
 * Drawer shell for displaying assignment details.
 * Resolves the correct renderer based on the assignment's primary ProductKind.
 *
 * @example
 * <AssignmentDetailDrawer
 *   isOpen={!!selectedAssignment}
 *   onClose={() => setSelectedAssignmentId(null)}
 *   assignment={selectedAssignment}
 *   orderId={dealData.id}
 * />
 */
export const AssignmentDetailDrawer: FC<Props> = ({ isOpen, onClose, assignment, orderId, workspaceId, order }) => {
  const { t } = useTranslation(['gallery', 'product', 'product_kind', 'category', 'actions'])
  const { realtyId } = useGalleryAssignment()
  const { userPropertyWorkspaceId } = useUserData()

  const matchedProduct = useMemo(() => {
    if (!assignment || !assignment.products.length) return null
    return choosePrimaryProduct(assignment.products)
  }, [assignment])

  const title = useMemo(() => {
    if (!matchedProduct) return ''
    return t(`product:p_${matchedProduct.id}`,
      t(`product_kind:${matchedProduct.kind}`,
        t(`category:${matchedProduct.category}`)
      )
    )
  }, [matchedProduct, t])

  const Renderer = useMemo(() => {
    if (!matchedProduct) return null
    return getAssignmentRenderer(matchedProduct)
  }, [matchedProduct])

  useEffect(() => {
    if (isOpen && assignment) {
      logAnalyticsEvent(AnalyticsEvent.ASSIGNMENT_DETAIL_DRAWER_OPENED, {
        assignmentId: assignment.id,
        orderId,
        realtyId,
        planId: userPropertyWorkspaceId,
        productId: matchedProduct?.id,
        productKind: matchedProduct?.kind,
        productCategory: matchedProduct?.category,
      })
    }
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = useCallback(() => {
    if (assignment) {
      logAnalyticsEvent(AnalyticsEvent.ASSIGNMENT_DETAIL_DRAWER_CLOSED, {
        assignmentId: assignment.id,
        orderId,
        realtyId,
        planId: userPropertyWorkspaceId,
        productId: matchedProduct?.id,
        productKind: matchedProduct?.kind,
        productCategory: matchedProduct?.category,
      })
    }
    onClose()
  }, [assignment, onClose, orderId, realtyId, userPropertyWorkspaceId, matchedProduct?.id, matchedProduct?.kind, matchedProduct?.category])

  return (
    <ResponsiveDrawer
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      backdrop="blurred"
      headerSx={GALLERY_DRAWER_HEADER_SX}
      subTitleSlot={assignment &&
        <Typography variant="text-md" fontWeight="450" color={GRAY_700}>
          {`#${assignment.id}`}
        </Typography>
      }
      actionsSlot={
        <Stack flexDirection="column" justifyContent="flex-end">
          <MUIButton type="secondaryBorder" borderStyle="rounded" onClick={handleClose}>
            {t('actions:Close')}
          </MUIButton>
        </Stack>
      }
    >
      {!!Renderer && !!assignment &&
        <AssignmentDetailDrawerContextProvider workspaceId={workspaceId}>
          <RendererErrorBoundary key={assignment?.id} assignmentId={assignment.id} productKind={matchedProduct?.kind}>
            <div className="fs-mask">

              <Renderer assignment={assignment} orderId={orderId} order={order} mainProductId={matchedProduct?.id} />

            </div>
          </RendererErrorBoundary>
        </AssignmentDetailDrawerContextProvider>
      }
    </ResponsiveDrawer>
  )
}
